import { cx } from '../primitives/cx.js';

export type SparklinePoint = {
  /** 0–1. The caller has already normalised; this component does no maths. */
  plot: number;
  /** Read out on hover and by a screen reader. */
  label: string;
};

export type SparklineProps = {
  points: readonly SparklinePoint[];
  /** Names the series for assistive tech — a single series needs no legend. */
  label: string;
  width?: number;
  height?: number;
  className?: string;
};

/**
 * A small trend, beside the number it belongs to.
 *
 * Follows the stat-tile contract: the line is drawn in the DE-EMPHASIS hue with
 * only the latest point in the accent. The line is context; the current value
 * is the point being made, and colouring the whole run accent would give equal
 * weight to six months of history and today.
 *
 * Deliberately not coloured by direction. The house rule says a fall is stated
 * plainly and never in red (§45, §53.7) — a student opening this screen after a
 * bad test must not be met with a red line. The sign and the words beside the
 * chart carry the direction instead, which also survives colourblindness and
 * greyscale printing.
 *
 * Does no arithmetic: `plot` arrives already 0–1 from `series()` in core, which
 * is where a rank gets inverted and a count gets normalised. One definition of
 * what a point means, and it is tested.
 */
export function Sparkline({
  points,
  label,
  width = 96,
  height = 28,
  className,
}: SparklineProps) {
  if (points.length === 0) return null;

  const pad = 4;
  const w = width - pad * 2;
  const h = height - pad * 2;

  // A single point has nowhere to travel, so it sits in the middle rather than
  // at x=0 with a line going nowhere.
  const x = (index: number) =>
    points.length === 1 ? pad + w / 2 : pad + (index / (points.length - 1)) * w;
  const y = (plot: number) => pad + (1 - plot) * h;

  const path = points.map((point, index) => `${x(index)},${y(point.plot)}`).join(' ');
  const last = points[points.length - 1] as SparklinePoint;

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={cx('shrink-0 overflow-visible', className)}
    >
      {points.length > 1 && (
        <polyline
          points={path}
          fill="none"
          stroke="var(--aj-line-strong)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Every point is hoverable, so the series can be read rather than only
          glanced at. Invisible and generously sized: the hit target is bigger
          than the mark, which at this scale it has to be. */}
      {points.map((point, index) => (
        <circle key={index} cx={x(index)} cy={y(point.plot)} r={6} fill="transparent">
          <title>{point.label}</title>
        </circle>
      ))}

      {/* The current value, in the accent. A ring in the surface colour keeps
          it legible where it overlaps the line. */}
      <circle
        cx={x(points.length - 1)}
        cy={y(last.plot)}
        r={4}
        fill="var(--aj-accent)"
        stroke="var(--aj-surface)"
        strokeWidth={2}
      />
    </svg>
  );
}
